import { useState } from "react";
import { Outlet, useNavigate } from "react-router";
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PeopleIcon from "@mui/icons-material/People";
import ListAltIcon from "@mui/icons-material/ListAlt";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useAuthenticatedUser } from "../AuthProvider";
import Logo from "components/Logo";

// Drawer width
const drawerWidth = 240;

export default function LoginLayout() {
  const navigate = useNavigate();
  // State for drawer open/close
  const [drawerOpen, setDrawerOpen] = useState(true);
  // State for user menu
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const userMenuOpen = Boolean(userMenuAnchorEl);

  const user = useAuthenticatedUser();

  // Toggle drawer
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Open user menu
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  // Close user menu
  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      handleUserMenuClose();
      navigate("/login");
    } catch (error: unknown) {
      console.error("ログアウトエラー:", error);
      alert(
        error instanceof Error
          ? error.message
          : "ログアウト中にエラーが発生しました",
      );
    }
  };

  // Drawer content
  const drawerContent = (
    <>
      <Toolbar /> {/* Spacer to push content below AppBar */}
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate("/users")}>
            <ListItemIcon>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText primary="会員一覧" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate("/factoring")}>
            <ListItemIcon>
              <ListAltIcon />
            </ListItemIcon>
            <ListItemText primary="申込一覧" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding sx={{ display: "none" }}>
          <ListItemButton onClick={() => navigate("/changereq")}>
            <ListItemIcon>
              <EditNoteIcon />
            </ListItemIcon>
            <ListItemText primary="会員情報変更申請一覧" />
          </ListItemButton>
        </ListItem>
      </List>
    </>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
          }}>
            <Logo />
          </Box>
          <IconButton
            color="inherit"
            aria-label="user menu"
            onClick={handleUserMenuOpen}
            size="large"
          >
            <AccountCircleIcon />
          </IconButton>
          <Menu
            anchorEl={userMenuAnchorEl}
            open={userMenuOpen}
            onClose={handleUserMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem disabled>{user.email}</MenuItem>
            <MenuItem onClick={handleLogout}>ログアウト</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="persistent"
        open={drawerOpen}
        sx={{
          display: { xs: "none", sm: "block" },
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            transform: drawerOpen ? "none" : `translateX(-${drawerWidth}px)`,
            transition: (theme) =>
              theme.transitions.create("transform", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* メインコンテンツの修正部分 */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: drawerOpen ? `calc(100% - ${drawerWidth}px)` : "100%",
          marginTop: "64px", // AppBar height
          marginLeft: drawerOpen ? 0 : `-${drawerWidth}px`,
          transition: (theme) =>
            theme.transitions.create(["margin", "width"], {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.duration.enteringScreen,
            }),
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
